import { titleFromSnakeOrCamel } from "lib/string";
import { useState } from "react";
import FeedbackButtons from "./FeedbackButtons";
import FeedbackSelect from "./FeedbackSelect";
import FeedbackText, { FeedbackCorrection } from "./FeedbackText";
import { FeedbackNumber } from "./FeedbackNumber";
import { HumanEvaluator } from "@/types/app/evaluator";
import { EvaluatorLog, Log } from "@/types/app/log";
import { getLatestEvaluatorLog } from "@/services/evaluators.service";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import { DescriptionList, DescriptionTerm } from "@components/llama/DescriptionList";
import { hlToast, ToastVariant } from "@library/Toast";

interface FeedbackBarProps {
  evaluator: HumanEvaluator;
  log: Log;
  onAddFeedback: (evaluatorLog: EvaluatorLog) => void;
}

export const FeedbackBar = ({ evaluator, log, onAddFeedback }: FeedbackBarProps) => {
  const evaluatorLogs =
    log.evaluator_logs?.filter((evaluatorLog) => evaluatorLog.evaluator.version_id === evaluator.version_id) || [];
  const latestEvaluatorLog = getLatestEvaluatorLog(evaluatorLogs);

  const [addingFeedback, setAddingFeedback] = useState(false);

  const handleError = (error: string) => {
    hlToast({
      title: "Failed to update Evaluation",
      description: error,
      variant: ToastVariant.Error,
    });
  };

  const options = evaluator.spec.options;
  let feedbackComponent: JSX.Element | null = null;

  switch (evaluator.spec.return_type) {
    case "select":
      if (options && options.length <= 5) {
        feedbackComponent = (
          <FeedbackButtons
            evaluator={evaluator}
            // Only show latest feedback for (non-multi) "select". Necessary to support legacy projects which can have multiple annotations.
            logs={latestEvaluatorLog ? [latestEvaluatorLog] : null}
            addingFeedback={addingFeedback}
            setAddingFeedback={setAddingFeedback}
            onAddFeedback={onAddFeedback}
            logId={log.id}
          />
        );
      } else {
        feedbackComponent = (
          <FeedbackSelect
            evaluator={evaluator}
            latestFeedback={latestEvaluatorLog}
            addingFeedback={addingFeedback}
            setAddingFeedback={setAddingFeedback}
            onAddFeedback={onAddFeedback}
            onError={handleError}
            logId={log.id}
          />
        );
      }
      break;
    case "multi_select":
      feedbackComponent = (
        <FeedbackButtons
          evaluator={evaluator}
          logs={evaluatorLogs}
          addingFeedback={addingFeedback}
          setAddingFeedback={setAddingFeedback}
          onAddFeedback={onAddFeedback}
          logId={log.id}
        />
      );
      break;
    case "text":
      if (evaluator.name === "correction") {
        feedbackComponent = (
          <FeedbackCorrection
            evaluator={evaluator}
            latestFeedback={latestEvaluatorLog}
            setAddingFeedback={setAddingFeedback}
            onAddFeedback={onAddFeedback}
            onError={handleError}
            log={log}
          />
        );
      } else {
        feedbackComponent = (
          <FeedbackText
            evaluator={evaluator}
            latestFeedback={latestEvaluatorLog}
            onError={handleError}
            setAddingFeedback={setAddingFeedback}
            onAddFeedback={onAddFeedback}
            log={log}
          />
        );
      }
      break;
    case "number":
      feedbackComponent = (
        <FeedbackNumber
          evaluator={evaluator}
          latestFeedback={latestEvaluatorLog}
          onError={handleError}
          setAddingFeedback={setAddingFeedback}
          onAddFeedback={onAddFeedback}
          log={log}
        />
      );
      break;
    default:
      const exhaustiveCheck: never = evaluator.spec.return_type;
      throw new Error(`Feedback type not supported: ${evaluator.spec.return_type}`);
  }
  return (
    <DescriptionList className="!grid-cols-[188px_auto] items-center truncate">
      <DescriptionTerm>
        <FileLabelWithPopover displayFile file={evaluator} size={20} />
      </DescriptionTerm>
      {feedbackComponent}
    </DescriptionList>
  );
};
